// General sub-agent runner. Spawn a fresh pi-agent-core Agent for a
// focused task, give it a system prompt + tool subset, optionally
// force structured output via a forced-call "return" tool, and stream
// lifecycle events back to a caller-supplied listener.
//
// All callers — the spawn_subagent / spawn_batch tools exposed to the
// main chat, future rule engines, ad-hoc scripts — go through this
// single entry point so abort propagation, max-turn guards, event
// throttling, and runtime sharing stay in one place.
//
// Design constraints learned from prior iterations:
//   * Children must share the chat's LLM runtime (model, proxy SW,
//     creds). Loading creds per child wastes RTT and breaks if the
//     user hasn't picked a model yet.
//   * Children rarely need the whole analytics schema in their
//     system prompt — they query the schema via tools instead.
//   * A child looping on a broken tool spec must be cut off; the
//     `maxTurns` guard fires `abort()` and surfaces a useful error.
//   * Events stream FAST (one per token). Callers throttle their
//     own UI updates — we just hand events through.

import { transcriptOf } from "./agentTranscript.js";
import { Agent } from "@earendil-works/pi-agent-core";
import { Type } from "@earendil-works/pi-ai";
import { buildAgentRuntime, contextCeilingBytes, CONTENT_CLAMP_WINDOW_FRACTION } from "./piChat.js";
import { compactMessages, compactionOptionsFor } from "./contextCompaction.js";
import { makeAfterToolCall } from "./chatAfterToolCall.js";
import { coerceJsonArg } from "./coerceJsonArg.js";
import { defaultThinkingLevelFor, resolveThinkingLevel } from "./modelCatalog.js";

// No per-child turn cap. Sub-agents run until they call return_result,
// finish on their own, or the user clicks STOP on their row (which
// cascades through the spawn-tool abort chain). The earlier cap kept
// clipping legitimate long investigations; manual stop is the escape
// hatch now.

// Nudge text sent when a forced-output child ends a turn with prose
// instead of the return_result tool call. Models occasionally miss the
// forced call on the first pass; one explicit reminder recovers most of
// them, which is far cheaper than failing the whole run and dropping the
// caller's work (e.g. a user's kernel save). See O-1354.
const RETURN_NUDGE =
    "You ended your turn without calling `return_result`, so your work was NOT captured " +
    "and will be discarded. Call `return_result` now with your final result — pass `result` " +
    "for an inline value, or `result_ref` for a row-shaped tool result you already produced. " +
    "Do not narrate; the tool call IS the answer.";

// Lazy runtimes per (role, model-override). Cleared on failure so the
// next attempt retries. "primary" is the user's chosen model; "task"
// is the optional cheap-model variant used by judges / extractors.
// A caller-supplied modelId (e.g. the cluster Describe picker bumping
// to a bigger context window) caches under its own key — each entry
// builds a different pi-ai Model (different id, possibly different
// headers), and we don't want to keep tearing one down to serve
// another.
const _runtimePromises = new Map();
// Bound the runtime cache. Keying by serverChatId (below) means the map can
// grow one entry per (role, model, chat), so a long session across many chats
// would otherwise accumulate a full pi-ai runtime per chat. Evicting a resolved
// runtime is harmless — any in-flight child still holds its own reference, and
// the next getSubAgentRuntime rebuilds cheaply (creds are cached).
const MAX_RUNTIME_CACHE = 24;
export function getSubAgentRuntime(role = "primary", modelId = null, serverChatId = null) {
    // serverChatId joins the cache key: a chat-scoped delegated child threads
    // the parent chat's server id so its LLM calls attribute to that chat in
    // the proxy audit log, while chat-less task callers (judges/extractors)
    // cache under the null-id runtime unchanged.
    const key = `${role}|${modelId || ""}|${serverChatId || ""}`;
    if (!_runtimePromises.get(key)) {
        _runtimePromises.set(key, buildAgentRuntime({ role, modelId, serverChatId }).catch((err) => {
            _runtimePromises.delete(key);
            throw err;
        }));
        // Map preserves insertion order — drop the oldest entry past the cap.
        while (_runtimePromises.size > MAX_RUNTIME_CACHE) {
            const oldest = _runtimePromises.keys().next().value;
            if (oldest === key) break;
            _runtimePromises.delete(oldest);
        }
    }
    return _runtimePromises.get(key);
}

// Drop all cached runtimes — next sub-agent run rebuilds against
// fresh creds. Wired into the model-picker save path so a switch to
// a new primary or task model takes effect immediately instead of
// waiting for a page refresh.
export function resetSubAgentRuntimes() {
    _runtimePromises.clear();
}

// Build a forced-output capture tool. The child must call it exactly
// once with its structured result; we use `afterToolCall: terminate`
// to stop the agent loop the moment it does. Schema is described in
// the tool description rather than enforced via typebox so callers
// can pass arbitrary JSON-schema-ish shapes without translating.
//
// Two ways to deliver the answer:
//   • `result` — inline value (anything JSON-serialisable).
//   • `result_ref` — tool_call_id of a row-shaped result the child
//     already produced via analytics_query or execute_python. The
//     spawning tool resolves the id against the shared
//     toolResultStore and re-records the rows under its own
//     tool_call_id, so the parent can pipe them into execute_python
//     via `from_tool_calls` without the child re-emitting them.
function makeReturnTool(outputSchema, onCapture) {
    const schemaText = outputSchema
        ? `\n\nReturn an object matching this shape:\n${typeof outputSchema === "string" ? outputSchema : JSON.stringify(outputSchema, null, 2)}`
        : "";
    return {
        name: "return_result",
        label: "Return result",
        description:
            `Call this exactly once when you have your final answer. Pass EITHER \`result\` (inline value) OR \`result_ref\` (tool_call_id of a prior row-shaped result you already fetched — typically from analytics_query or execute_python in your own context). Prefer \`result_ref\` when your answer IS rows you just fetched; it lets the parent reuse the rows without you re-emitting them. After calling this, stop — do not emit further messages.${schemaText}`,
        // `result` stays Type.Unknown — it's genuinely polymorphic (prose
        // string, object, array, or scalar depending on the sub-agent), so
        // unlike test_kernel's `kernel` it can't be a typed object. The
        // double-encoding coercion lives in execute() (gated on outputSchema)
        // instead; when outputSchema is set the expected object shape is
        // steered via the description above.
        parameters: Type.Object({
            result: Type.Optional(Type.Unknown({
                description: "The final inline result value. When an output shape is given above, pass it as an inline JSON object — NOT a JSON-encoded string.",
            })),
            result_ref: Type.Optional(Type.String({
                description: "tool_call_id of a row-shaped result from a prior tool call in YOUR context. The spawning tool will resolve this and surface the rows to the parent.",
            })),
        }),
        async execute(_id, args) {
            // Forced-output children (outputSchema set) are contracted to
            // return a structured object/array. Some model families
            // (notably Claude) double-encode such tool args as a JSON
            // string — `result: "{...}"` instead of the object — the same
            // cross-model asymmetry coerceJsonArg defends at the
            // render_block/canvas boundary. Without this, a stringified
            // kernel reaches runKernelExtraction as an escaped string and
            // is rejected ("extraction agent did not return a kernel").
            // Decode it back so structured callers get the object they
            // asked for regardless of model. Prose-mode children (no
            // outputSchema) keep their raw string — coerceJsonArg only
            // touches JSON-ish strings anyway, but gating keeps free-text
            // answers that happen to look JSON-ish untouched.
            const value = outputSchema ? coerceJsonArg(args?.result) : args?.result;
            onCapture({ value, refId: args?.result_ref });
            return { content: [{ type: "text", text: "captured" }] };
        },
    };
}

// Build a sub-agent and start its run, but don't await completion.
// Returns { child, abort, promise } synchronously (well, after the
// runtime fetch). The promise resolves with the same { status, result,
// resultRef, messages, error } shape runSubAgent returns.
//
// Useful when the caller wants to race the child's completion against
// another signal — e.g. spawn_subagent listening for a parent steer
// to interrupt mid-await. For callers that just want the result,
// `runSubAgent` (below) is a thin wrapper that awaits the promise.
//
// On runtime failure (creds missing, proxy down, etc.) returns a
// handle with `child: null` and a pre-resolved error promise. Callers
// can switch on `handle.child` to decide whether to set up steer
// races or just await the immediate failure.
export async function startSubAgent({
    task,
    systemPrompt = "",
    tools = [],
    outputSchema = null,
    signal,
    onEvent,
    onChildReady,
    role = "primary",
    // Explicit model id for this child — overrides the role's default
    // (primary = the user's chosen model, task = the cheap variant).
    // Same provider/creds either way; only the model differs.
    model = null,
    // How many times to re-prompt a forced-output child (outputSchema
    // set) that ended a turn without calling return_result. Default 1:
    // one transient miss shouldn't fail the whole run. Ignored when no
    // outputSchema is set (return_result is opt-in there).
    maxResultNudges = 1,
    // Optional pre-seeded message history. Used by spawn_subagent /
    // spawn_batch to stage a synthetic prior tool result so a slice
    // of the parent's row data lands directly in the child's context
    // without the parent re-emitting it in tool-call args. Messages
    // should already include `synthesized: true` so compaction can
    // stub them aggressively if the chat keeps growing.
    initialMessages = null,
    // Thinking depth to mirror onto the child — e.g. the parent chat's
    // current `thinkingLevel`. Primary-role spawns pass the parent's
    // live level so children reason at the same depth the user picked
    // (the main chat applies it on itself via agent.state.thinkingLevel).
    // null falls back to the model's default depth for primary-role
    // children (see the apply site below); task-role children are left
    // at pi-agent-core's own default.
    thinkingLevel = null,
    // Server chat UUID of the spawning chat (chat persistence). Threaded into
    // the child's credential so its LLM proxy calls attribute to that chat in
    // the audit log. null for chat-less callers (judges, extractors).
    serverChatId = null,
}) {
    let runtime;
    try {
        runtime = await getSubAgentRuntime(role, model, serverChatId);
    } catch (err) {
        return {
            child: null,
            abort: () => {},
            promise: Promise.resolve({ status: "error", error: err, result: null, resultRef: null, messages: [] }),
        };
    }

    // The return_result tool is injected even when no outputSchema is
    // set so prose-mode sub-agents can OPPORTUNISTICALLY surface a
    // row-shaped result by ref. If they don't call it, the lastAssistant
    // text fallback below still applies — strictly additive.
    let captured = null;
    let capturedRef = null;
    const childTools = [
        ...tools,
        makeReturnTool(outputSchema, ({ value, refId }) => {
            if (value !== undefined) captured = value;
            if (typeof refId === "string" && refId.trim()) capturedRef = refId.trim();
        }),
    ];

    // Inner abort merges the outer signal with the local abort handle.
    // Important: when the outer signal fires we must also call
    // `child.abort()` — pi-agent-core's Agent has its own abort
    // surface and an AbortController alone doesn't reach it. Without
    // this the agent loop keeps running even after the caller asked
    // it to stop.
    const childController = new AbortController();
    let child = null;
    const onOuterAbort = () => {
        childController.abort();
        try { child?.abort(); } catch { /* noop */ }
    };
    if (signal) {
        if (signal.aborted) {
            childController.abort();
        } else {
            signal.addEventListener("abort", onOuterAbort, { once: true });
        }
    }

    // Always terminate the loop on return_result. With an outputSchema
    // the child is REQUIRED to call it; without one, calling it is a
    // valid opt-in short-circuit for the row-ref case.
    const terminateName = "return_result";

    child = new Agent({
        initialState: {
            model: runtime.model,
            tools: childTools,
            systemPrompt: systemPrompt || undefined,
            // Seed the child's history with any staged synthetic
            // messages (parent → child row handoff). Empty when the
            // caller didn't stage anything — pi-agent-core falls
            // back to an empty array.
            messages: Array.isArray(initialMessages) && initialMessages.length ? initialMessages : undefined,
        },
        convertToLlm: runtime.convertToLlm,
        // The child's conversation compacted against its own model, prompt
        // and tools before every call, as the main chat's is, so a child
        // that reads past its window keeps working from what it read. Only
        // compaction: the main chat's eviction passes prune the chat's tool
        // result store by the chat's own history, which a child's is not.
        transformContext: async (messages) => compactMessages(messages, compactionOptionsFor(child)),
        getApiKey: runtime.getApiKey,
        // Composed through makeAfterToolCall so children get the same
        // oversized-result clamp as the main chat (children share the
        // parent's tool instances, and their transcripts flow back
        // into the parent's items), tuned to the CHILD's own model —
        // task-role children may run a smaller-context model than the
        // parent. suggest_kernel termination stays off — children
        // terminate on return_result instead.
        afterToolCall: makeAfterToolCall({
            extraAfterToolCall: async (ctx) => (ctx.toolCall?.name === terminateName ? { terminate: true } : undefined),
            maxContentBytes: () => Math.ceil(
                contextCeilingBytes(child?.state?.model?.contextWindow ?? runtime.model?.contextWindow)
                * CONTENT_CLAMP_WINDOW_FRACTION,
            ),
        }),
    });

    // Mirror the requested thinking depth onto the child — a plain
    // state assignment, same as the main chat does in ChatConsole. Set
    // before the first prompt() below so it takes effect on turn one.
    // A truthy explicit level is resolved through resolveThinkingLevel —
    // the same guard the main chat applies — so an explicit "off" on a
    // model whose endpoint mandates reasoning (e.g. Fable) falls back to
    // the model default instead of producing a reasoning-disabled request
    // the endpoint rejects with a 400 ("Reasoning is mandatory…"). Every
    // other level passes through unchanged.
    //
    // When no level was threaded, fall back to the MODEL's default depth
    // for primary-role children only. "primary" is the user's chosen
    // model, so a directly-invoked primary sub-agent (the kernel
    // extractor, commit_to_kernel) should reason at the same depth the
    // main chat defaults to — "medium" for a flagship reasoning model
    // like Fable — rather than inheriting pi-agent-core's
    // thinking-disabled default (O-1994 threaded thinking through the
    // spawn_* tools but never reached these direct callers). Task-role
    // judges stay off: their cheap non-flagship model already resolves
    // to "off" here, and we don't want classification work to think.
    if (thinkingLevel) {
        child.state.thinkingLevel = resolveThinkingLevel(runtime.creds?.provider, runtime.modelId, thinkingLevel);
    } else if (role === "primary") {
        const fallback = defaultThinkingLevelFor(runtime.creds?.provider, runtime.modelId);
        if (fallback && fallback !== "off") {
            child.state.thinkingLevel = fallback;
        }
    }

    // Hand the child reference to the caller before we start so the
    // spawning tool can hook up live observability — read messages,
    // grab the abort handle, anything that wants the Agent itself.
    try { onChildReady?.(child); } catch { /* observability mustn't break the run */ }

    const unsubscribe = child.subscribe((event) => {
        try { onEvent?.(event); } catch { /* listener errors must not break the loop */ }
    });

    const captureMissing = () => captured == null && capturedRef == null;
    const promise = (async () => {
        let error = null;
        try {
            await child.prompt(task);
            await child.waitForIdle();
            // Forced-output children sometimes end a turn with prose
            // instead of the return_result call. Re-prompt a bounded
            // number of times before giving up — recovers the common
            // transient miss without an unbounded loop.
            let nudges = 0;
            while (
                outputSchema
                && captureMissing()
                && !childController.signal.aborted
                && nudges < maxResultNudges
            ) {
                nudges++;
                await child.prompt(RETURN_NUDGE);
                await child.waitForIdle();
            }
        } catch (err) {
            error = err;
        } finally {
            unsubscribe();
            if (signal) signal.removeEventListener("abort", onOuterAbort);
        }

        // The conversation alone; the child's prompt and tools stay with it.
        const messages = transcriptOf(child);
        const status = error
            ? "error"
            : childController.signal.aborted
                ? "aborted"
                : "done";

        // The child's final assistant prose (null if it never produced any),
        // used as the prose-mode result fallback below.
        const finalText = lastAssistantText(messages);

        // pi-agent-core records a per-turn LLM failure on the message's
        // `errorMessage` field instead of rejecting prompt() — the main chat
        // reads the SAME terminal field at agent_end (ChatConsole). So a
        // request the proxy refused — e.g. an invalid thinking config → HTTP
        // 400 — surfaces as a "done" run that captured nothing, which the
        // branches below would mislabel as a transient "stopped without
        // returning a recipe, try again". Surface the real error instead.
        //
        // Key on the TRUE last message, not "any error anywhere in history":
        //   • error early, then recovered with text  → last msg is the text,
        //     no terminal error → keep the recovered result (prose-mode
        //     children never call return_result, so captureMissing() alone
        //     can't tell success from failure).
        //   • text early, then a terminal failure    → last msg carries the
        //     error → surface it, don't return the stale earlier text as if
        //     the run succeeded.
        const terminalError = captureMissing()
            ? errorMessageOf(messages[messages.length - 1])
            : null;
        if (status === "done" && terminalError) {
            return {
                status: "error",
                reason: "llm_error",
                error: new Error(terminalError),
                result: null,
                resultRef: null,
                lastAssistantText: finalText,
                messages,
            };
        }

        // No explicit capture → return the last assistant text so callers
        // that didn't ask for structured output still get something useful.
        // `capturedRef` rides alongside `result` when set so spawning tools
        // can re-record the referenced rows under their own tool_call_id.
        let result = captured;
        if (result == null && capturedRef == null && !outputSchema) {
            result = finalText;
        }
        if (result == null && capturedRef == null && outputSchema && status === "done") {
            // Child said "done" but never called return_result, even after
            // nudging. Surface a tagged error so callers can show actionable
            // copy instead of this internal string, and hand back the final
            // prose + transcript so the failure is diagnosable rather than
            // vanishing (the messages used to be dropped by every caller).
            return {
                status: "error",
                reason: "no_return_result",
                error: new Error("sub-agent finished without calling return_result"),
                result: null,
                resultRef: null,
                lastAssistantText: finalText,
                messages,
            };
        }

        return { status, result, resultRef: capturedRef, messages, error };
    })();

    return {
        child,
        abort: () => { try { child?.abort(); } catch { /* noop */ } },
        promise,
    };
}

// Convenience wrapper. Same call shape as before — start the child
// and await its result. Most callers that don't need mid-run control
// should stay on this.
export async function runSubAgent(opts) {
    const handle = await startSubAgent(opts);
    return handle.promise;
}

// The LLM `errorMessage` pi-agent-core tagged onto a single message (a
// per-turn call failure it swallows rather than throwing), coerced to a
// string. null when the message carries no error.
function errorMessageOf(message) {
    const em = message?.errorMessage;
    if (!em) return null;
    return typeof em === "string" ? em : String(em);
}

function lastAssistantText(messages) {
    for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.role !== "assistant") continue;
        const text = Array.isArray(m.content)
            ? m.content.filter((c) => c?.type === "text").map((c) => c.text).join("")
            : typeof m.content === "string"
                ? m.content
                : "";
        if (text) return text;
    }
    return null;
}

// Run `tasks` (array of zero-arg async functions) with at most
// `limit` in flight at a time. Returns a Promise<settled[]> in input
// order. Each entry is `{ ok: true, value }` or `{ ok: false, error }`.
// Used by spawn_batch and any future fan-out.
export async function runWithConcurrency(tasks, limit) {
    if (!tasks.length) return [];
    const results = new Array(tasks.length);
    let next = 0;
    const workerCount = Math.max(1, Math.min(limit, tasks.length));
    const workers = new Array(workerCount).fill(0).map(async () => {
        while (true) {
            const i = next++;
            if (i >= tasks.length) return;
            try {
                results[i] = { ok: true, value: await tasks[i]() };
            } catch (error) {
                results[i] = { ok: false, error };
            }
        }
    });
    await Promise.all(workers);
    return results;
}
